import React, { useMemo, useState, useEffect } from 'react';
import { HiPlus, HiPencil, HiTrash, HiX, HiSearch } from 'react-icons/hi';
import ConfirmDialog from './ConfirmDialog';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import { SkeletonTable } from './Skeleton';
import apiService from '../services/api';
import { normalizeApiData } from '../utils/apiHelpers';
import PhoneInput from './PhoneInput';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSAO_MAP } from '../types';
import './Clients.css';

const Clients = () => {
  const { userRole, loading: permissionsLoading } = usePermissions();
  const isClientsAdmin = useMemo(() => {
    if (permissionsLoading) return false;
    if (!userRole) return false;
    return PERMISSAO_MAP[Number(userRole.permissaos_id)] === 'Admin';
  }, [userRole, permissionsLoading]);

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    endereco: '',
    documento: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const { notifications, removeNotification, showSuccess, showWarning } = useNotification();

  useEffect(() => {
    loadClients();
  }, [searchTerm]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await apiService.getClients({
        search: searchTerm,
      });
      const clientsList = normalizeApiData(data);
      setClients(clientsList);
        setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setClients([]);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
    if (editingId) {
        await apiService.updateClient(editingId, {
          nome: formData.nome,
          email: formData.email,
          telefone: formData.telefone,
          documento: formData.documento,
          endereco: formData.endereco,
        });
        showSuccess('Cliente atualizado com sucesso!');
    } else {
        await apiService.createClient({
          nome: formData.nome,
          email: formData.email,
          telefone: formData.telefone,
          documento: formData.documento,
          endereco: formData.endereco,
        });
        showSuccess('Cliente criado com sucesso!');
    }
    handleCloseModal();
      loadClients();
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      showWarning(error.message || 'Erro ao salvar cliente');
    }
  };

  const handleEdit = (client) => {
    setFormData({
      nome: client.nome || client.name || '',
      email: client.email || '',
      telefone: client.telefone || client.phone || '',
      documento: client.documento || '',
      endereco: client.endereco || client.address || '',
    });
    setEditingId(client.id);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!isClientsAdmin) return;
    setClientToDelete(id);
    setShowConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (!clientToDelete) {
      showWarning('ID do cliente não encontrado');
      setShowConfirmDialog(false);
      return;
    }
    if (!isClientsAdmin) return;

    try {
      console.log('Excluindo cliente com ID:', clientToDelete);
      await apiService.deleteClient(clientToDelete);
    setShowConfirmDialog(false);
    setClientToDelete(null);
    showSuccess('Cliente excluído com sucesso!');
      loadClients();
    } catch (error) {
      console.error('Erro ao excluir cliente:', error);
      console.error('Detalhes do erro:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      const errorMessage = error?.message || 'Erro ao excluir cliente. Verifique se o cliente não está sendo usado em outros registros (invoices, boxes, etc.).';
      showWarning(errorMessage);
      setShowConfirmDialog(false);
      setClientToDelete(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ nome: '', email: '', telefone: '', documento: '', endereco: '' });
    setEditingId(null);
  };

  // Filtros e paginação
  // A filtragem já é feita pela API, mas mantemos aqui para compatibilidade
  const filteredClients = Array.isArray(clients) ? clients.filter(client =>
    (client.nome || client.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (client.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (client.telefone || client.phone || '').includes(searchTerm)
  ) : [];

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, endIndex);

  return (
    <div className="page-container">
      <div className="breadcrumb">
        <span>Painel</span>
        <span>Clientes</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Clientes</h1>
          <p>Gerencie seus clientes</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <HiPlus size={20} />
          Novo Cliente
        </button>
      </div>

      <div className="page-actions">
        <div className="search-box">
          <HiSearch className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Buscar clientes..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <div className="card">
        {loading ? (
          <SkeletonTable rows={8} columns={6} />
        ) : paginatedClients.length === 0 ? (
          <div className="empty-state">
            <p>{searchTerm ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Telefone</th>
                    <th>Documento</th>
                    <th>Endereço</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedClients.map((client, index) => (
                    <tr key={client.id}>
                      <td>{startIndex + index + 1}</td>
                      <td><strong>{client.nome || client.name || 'N/A'}</strong></td>
                      <td>{client.email || 'N/A'}</td>
                      <td>{client.telefone || client.phone || 'N/A'}</td>
                      <td>{client.documento || 'N/A'}</td>
                      <td>{client.endereco || client.address || 'N/A'}</td>
                      <td>
                        <div className="actions">
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => handleEdit(client)}
                            title="Editar"
                          >
                            <HiPencil size={18} />
                          </button>
                          {isClientsAdmin && (
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDelete(client.id)}
                              title="Excluir"
                            >
                              <HiTrash size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="table-footer">
              <div className="pagination-info">
                {filteredClients.length > 0 ? (
                  <span>
                    Mostrando {(currentPage - 1) * itemsPerPage + 1} a {Math.min(currentPage * itemsPerPage, filteredClients.length)} de {filteredClients.length} registro{filteredClients.length !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span>Nenhum registro encontrado</span>
                )}
              </div>
              <div className="items-per-page">
                <span>Mostrar:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                <span className="pagination-info">
                  Página {currentPage} de {totalPages || 1}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Editar Cliente' : 'Novo Cliente'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <HiX size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Nome</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                  placeholder="Digite o nome do cliente"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="email@exemplo.com"
                />
              </div>
              <div className="form-group">
                <label>Telefone</label>
                <PhoneInput
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  required
                  placeholder="Número do telefone"
                />
              </div>
              <div className="form-group">
                <label>Documento</label>
                <input
                  type="text"
                  value={formData.documento}
                  onChange={(e) => setFormData({ ...formData, documento: e.target.value })}
                  placeholder="CPF/CNPJ"
                />
              </div>
              <div className="form-group">
                <label>Endereço</label>
                <input
                  type="text"
                  value={formData.endereco}
                  onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  required
                  placeholder="Cidade, Estado"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este cliente?"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirmDialog(false);
          setClientToDelete(null);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      <div className="notifications-container">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Clients;